import React, {FC} from 'react';

import CardButton from '@/components/CardButton';
import ScrollContainer from '@/components/ScrollContainer';
import Space from '@/components/Space';
import TransparentButton from '@/components/TransparentButton';
import WarningBox from '@/components/WarningBox';
import {tipChain} from '@/modules/Tip/constants';
import {useVerifyAddressState} from '@/modules/Tip/VerifyAddress/useVerifyAddressState';
import {
  EtherscanLink,
  JiffyscanLink,
  TransactionError,
} from '@/modules/Transactions';
import {PasskeyError} from '@/services/passkey';
import {useThemedStyles} from '@/theme';
import {isOSXBrowser} from '@/utils/platform';
import {getUserDisplayName} from '@/utils/user';

import {styles} from './VerifyAddressError.style';

const VerifyAddressError: FC = () => {
  const style = useThemedStyles(styles);

  const {transaction, resetTransactionDetails, user, close} =
    useVerifyAddressState();
  const {error, txHash, userOpHash} = transaction;

  const seeDetailsAction = txHash ? (
    <Space mt="xs">
      <EtherscanLink txHash={txHash} chainId={tipChain.id} />
    </Space>
  ) : userOpHash ? (
    <Space mt="xs">
      <JiffyscanLink userOpHash={userOpHash} chainId={tipChain.id} />
    </Space>
  ) : null;

  return (
    <ScrollContainer contentStyle={style.content}>
      {(() => {
        if (error instanceof PasskeyError && error.status === 'MISSING_BLOB') {
          return (
            <WarningBox
              title={{id: 'collect.error.missingBlob'}}
              message={{
                id: isOSXBrowser
                  ? 'collect.error.missingBlob.osx'
                  : 'collect.error.missingBlob.default',
              }}
            />
          );
        }

        if (
          error instanceof TransactionError &&
          error.status === 'WRONG_PASSKEY_SIGNER'
        ) {
          return (
            <WarningBox
              title={{id: 'collect.error.wrongSigner'}}
              message={
                user && {
                  id: 'collect.error.wrongSigner.message',
                  values: {
                    user: getUserDisplayName(user),
                  },
                }
              }
            />
          );
        }

        if (
          error instanceof TransactionError &&
          error.status === 'TRANSACTION_REVERTED'
        ) {
          return (
            <WarningBox
              title={{id: 'tip.verify.error.reverted'}}
              action={seeDetailsAction}
            />
          );
        }

        return (
          <WarningBox
            title={{id: 'tip.verify.error.generic'}}
            message={{id: 'tip.verify.error.generic.message'}}
            action={seeDetailsAction}
          />
        );
      })()}

      <CardButton onPress={resetTransactionDetails} text={{id: 'tryAgain'}} />
      <TransparentButton
        style={style.secondaryAction}
        onPress={close}
        text={{id: 'cancel'}}
      />
    </ScrollContainer>
  );
};

export default VerifyAddressError;
